/**
 * 📌 API CLIENT - Clase base para peticiones HTTP
 * PR-22: Consolidado — usa la instancia canónica de @/services/api-client.
 * Una sola instancia axios, un solo set de interceptors.
 */

import { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios';
// P12-TEST1A-FE4: import the CANONICAL runtime-config-aware client explicitly by file.
// `@/services/api-client` (extension-less) resolves to the `.js` compat shim
// (→ _deprecated OptimizedAPIClient, hard-coded http://localhost:3002/api) because Vite's default
// resolve order is `.js` before `.ts`. The explicit `.ts` binds the canonical axios instance so
// this facade (and the auth flow that uses it) resolves the API base URL via runtimeConfig.
import { apiClient } from '@/services/api-client.ts';

export interface ApiResponse<T = any> {
  data: T;
  status: number;
  message?: string;
  errors?: any[];
}

export class ApiClient {
  /**
   * Instancia compartida — es la misma de @/services/api-client.
   * No se crea una nueva instancia aquí. Interceptors centralizados en api-client.ts.
   */
  private static instance: AxiosInstance = apiClient;

  static async get<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.instance.get(url, config);
    // PR-22: La instancia canónica desencapsula { success, data } automáticamente.
    // Re-wrap para mantener contrato ApiResponse<T> de enterprise-services.
    return { data: response.data, status: response.status } as ApiResponse<T>;
  }

  static async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.instance.post(url, data, config);
  }

  static async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.instance.put(url, data, config);
  }

  static async delete<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.instance.delete(url, config);
  }

  static async patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.instance.patch(url, data, config);
  }

  static getInstance(): AxiosInstance {
    return this.instance;
  }
}

export default ApiClient;